import type { AdvertisedEndpoint, DesktopBridge, DesktopWslState } from "@t3tools/contracts";

import type { MessageKey } from "~/i18n";

type WslEnableBridge = Pick<DesktopBridge, "setWslBackendEnabled" | "setWslDistro" | "setWslOnly">;

/**
 * A QR code encoding a loopback URL makes the scanning device dial itself, so
 * loopback endpoints stay copyable from the endpoint menu but are never
 * offered as QR targets.
 */
export function isQrShareableEndpoint(endpoint: AdvertisedEndpoint): boolean {
  return endpoint.status !== "unavailable" && endpoint.reachability !== "loopback";
}

export type QrEndpointOption = {
  /** Unique per endpoint instance (AdvertisedEndpoint.id); safe as a React key. */
  readonly id: string;
  /**
   * Stable per endpoint *type* (endpointDefaultPreferenceKey). Multiple
   * endpoints can share one, so it is only used to match the saved default.
   */
  readonly preferenceKey: string;
  /** False for endpoints that stay copyable but must never render as a QR. */
  readonly qrShareable: boolean;
};

/**
 * Resolves which endpoint the share panel shows: the user's explicit pick,
 * else the saved default endpoint, else the first QR-shareable option (so the
 * panel never opens on a loopback QR), else the first option. A stale
 * selectedId (endpoint disappeared) falls back rather than blanking the panel.
 */
export function selectQrEndpointOption<T extends QrEndpointOption>(
  options: ReadonlyArray<T>,
  selectedId: string | null,
  defaultPreferenceKey: string | null,
): T | null {
  return (
    (selectedId !== null ? options.find((option) => option.id === selectedId) : undefined) ??
    (defaultPreferenceKey !== null
      ? options.find((option) => option.preferenceKey === defaultPreferenceKey)
      : undefined) ??
    options.find((option) => option.qrShareable) ??
    options[0] ??
    null
  );
}

/**
 * Department chips on the Connections catalog. `id` is sent as `?dept=`
 * (except `all`, which means no department filter). Labels stay short so the
 * row fits above the card grid.
 */
export const TOOLKIT_DEPARTMENT_FILTERS = [
  { id: "all", labelKey: "settings.connections.dept.all" },
  { id: "comms", labelKey: "settings.connections.dept.comms" },
  { id: "dev", labelKey: "settings.connections.dept.dev" },
  { id: "work", labelKey: "settings.connections.dept.work" },
  { id: "crm", labelKey: "settings.connections.dept.crm" },
  { id: "data", labelKey: "settings.connections.dept.data" },
  { id: "finance", labelKey: "settings.connections.dept.finance" },
  { id: "ai", labelKey: "settings.connections.dept.ai" },
  { id: "other", labelKey: "settings.connections.dept.other" },
] as const satisfies ReadonlyArray<{ id: string; labelKey: MessageKey }>;

export type ToolkitDepartmentFilterId = (typeof TOOLKIT_DEPARTMENT_FILTERS)[number]["id"];

/**
 * The Connections tab pages the Composio toolkit catalog with offset/limit.
 * The server reply carries `{ total, offset, limit }` for the page just loaded;
 * more remain whenever the count already appended is still short of `total`.
 * `loadedCount` is how many cards the grid currently holds (append-only), so
 * this is robust even if the final page returns fewer than `limit` rows.
 */
export function hasMoreToolkits(loadedCount: number, total: number): boolean {
  return loadedCount < total;
}

/**
 * The offset for the NEXT page request is simply how many cards we've already
 * appended — the server slices `[offset, offset + limit)` over the (filtered)
 * catalog, so contiguous appends never skip or duplicate a row.
 */
export function nextToolkitOffset(loadedCount: number): number {
  return loadedCount;
}

export async function applyWslEnableSelection(input: {
  readonly bridge: WslEnableBridge;
  readonly mode: "both" | "wsl-only";
  readonly nextDistro: string | null;
  readonly persistedDistro: string | null;
}): Promise<DesktopWslState> {
  const { bridge, mode, nextDistro, persistedDistro } = input;

  // Stage every preference before enabling. The desktop only relaunches for
  // mode/distro changes while WSL is active, so the final enable observes the
  // complete selection and is the only call that may relaunch.
  await bridge.setWslOnly(mode === "wsl-only");
  if (persistedDistro !== nextDistro) {
    await bridge.setWslDistro(nextDistro);
  }
  return await bridge.setWslBackendEnabled(true);
}
